/**
 * useInsightsSearchFilters — URL-bound filter state for Insights routes.
 *
 * Replaces `useState<InsightsFilters>(DEFAULT_FILTERS)` across every
 * Insights route (`/insights`, `/insights/adoption`, `/insights/quality`,
 * `/insights/cost`, `/insights/agents-knowledge`, `/insights/value`,
 * `/insights/risk`).
 *
 * Per resolved decision #9 (founder-locked, see
 * docs/testing/PHASE2_RESOLVED_DECISIONS_2026-05-14.md) — the
 * FilterBar docstring at filter-bar.tsx:7-8 promises URL binding, but
 * the implementation used local `useState`, so filters reset on tab
 * switch, page refresh, and direct deep-link visits. INS-FILTER-PERSIST-001.
 *
 * The fix is one shared hook + a Zod validator applied per route via
 * `validateSearch`. The FilterBar component stays a controlled
 * `{ filters, onChange }` component (so it is still reusable in
 * non-URL-bound contexts like sysadmin previews or storybook).
 *
 * URL serialization rules:
 *   - default values (range='7d', everything else 'all') are NOT written
 *     to the URL — keeps URLs clean for the common case
 *   - any non-default value IS written
 *   - unknown / invalid values are stripped at `validateSearch` parse
 *
 * Pattern mirrors the existing W1a-01 users-list URL binding at
 * `routes/users/index.tsx:73-101`.
 */
import { z } from 'zod';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { DEFAULT_FILTERS, type InsightsFilters, type TimeRange } from './filter-bar';

/**
 * Zod schema for the search-param shape of every Insights route.
 *
 * Short keys (`r`, `d`, `a`, `m`) keep URLs short on shareable links
 * — the FilterBar dropdown values would otherwise bloat the URL with
 * `?range=7d&department=Engineering&agentId=agent-ask-argus&modelId=qwen-72b-awq`
 * vs `?d=Engineering&a=agent-ask-argus&m=qwen-72b-awq`.
 *
 * `.optional()` everywhere = missing = default. `range` is constrained
 * to the four real backend buckets + 'custom' to match the
 * TimeRange union at filter-bar.tsx:19.
 */
export const insightsSearchSchema = z.object({
  r: z.enum(['24h', '7d', '30d', '90d', 'custom']).optional(),
  d: z.string().optional(),
  a: z.string().optional(),
  m: z.string().optional(),
});

export type InsightsSearch = z.infer<typeof insightsSearchSchema>;

/**
 * Reusable `validateSearch` for every Insights route. Strips unknown
 * keys so accidental `?utm_source=…` etc. do not poison the URL.
 */
export function validateInsightsSearch(raw: unknown): InsightsSearch {
  const result = insightsSearchSchema.safeParse(raw ?? {});
  // On parse failure, fall back to empty (= all defaults). This matches
  // the spirit of W1a-01: deep-links should never throw, they should
  // degrade gracefully to the default view.
  return result.success ? result.data : {};
}

/**
 * Convert URL search params → InsightsFilters (with defaults).
 */
function searchToFilters(sp: InsightsSearch): InsightsFilters {
  return {
    range: (sp.r ?? DEFAULT_FILTERS.range) as TimeRange,
    department: sp.d ?? DEFAULT_FILTERS.department,
    agentId: sp.a ?? DEFAULT_FILTERS.agentId,
    modelId: sp.m ?? DEFAULT_FILTERS.modelId,
  };
}

/**
 * Convert InsightsFilters → URL search params, omitting defaults.
 *
 * Default range is `'7d'` and default for everything else is `'all'`.
 * Writing only non-defaults keeps URLs short and means a "clean"
 * insights URL (no params) is the same as the default view.
 */
function filtersToSearch(f: InsightsFilters): InsightsSearch {
  const out: InsightsSearch = {};
  if (f.range !== DEFAULT_FILTERS.range) out.r = f.range;
  if (f.department !== DEFAULT_FILTERS.department) out.d = f.department;
  if (f.agentId !== DEFAULT_FILTERS.agentId) out.a = f.agentId;
  if (f.modelId !== DEFAULT_FILTERS.modelId) out.m = f.modelId;
  return out;
}

/**
 * Drop-in replacement for `useState<InsightsFilters>(DEFAULT_FILTERS)`.
 *
 * Returns the current filters (read from URL) and a setter that writes
 * back to the URL via TanStack Router's `useNavigate`. `replace: true`
 * means changing a filter does NOT add a history entry — pressing
 * Back from /insights with a filter applied should still take the
 * user to wherever they came from, not to the same /insights with
 * fewer filters.
 *
 * @param routeId - one of the seven Insights route IDs, e.g. '/insights/'
 *                  or '/insights/adoption'. Required because TanStack
 *                  Router's `useSearch({ from })` is route-specific.
 */
export function useInsightsSearchFilters(
  routeId:
    | '/insights/'
    | '/insights/adoption'
    | '/insights/quality'
    | '/insights/cost'
    | '/insights/agents-knowledge'
    | '/insights/value'
    | '/insights/risk',
): [InsightsFilters, (next: InsightsFilters) => void] {
  const sp = useSearch({ from: routeId });
  const navigate = useNavigate();

  // routeId is also the navigation target — TanStack Router accepts the
  // same string for `from` (useSearch) and `to` (navigate).
  const filters = searchToFilters(sp as InsightsSearch);

  // TanStack Router uses `/insights/` as the route ID (with trailing
  // slash) for the index route but `/insights` (no trailing slash) as
  // the navigation target. Strip the trailing slash to keep
  // `navigate({ to })` type-safe.
  const navigateTo = routeId === '/insights/' ? '/insights' : routeId;

  const setFilters = useCallback(
    (next: InsightsFilters) => {
      navigate({
        // The type system wants a `to` constrained to known routes; the
        // route IDs we accept here are all real registered routes, so
        // the cast is safe.
        to: navigateTo,
        search: filtersToSearch(next),
        replace: true,
      });
    },
    [navigate, navigateTo],
  );

  return [filters, setFilters];
}
